/**
 * frontend/src/PersonaOnboarding.jsx
 * SoundDAO — Full-screen persona selection at /onboarding.
 *
 * Three cards: Player (Rex), Creator (Ivy), Collector (Atlas)
 * On confirm: POST /api/users/onboard → show RewardMoment → route to dashboard
 *
 * Sprint 6-Zero Milestone 3: renamed from LandingPage.jsx. The old name implied
 * a public marketing page and cost this project three months — the value-prop
 * copy below shipped in May but only signed-in users could ever reach it. The
 * public front door now lives in pages/PublicLanding.jsx. Behaviour here is
 * unchanged; this is a rename plus this comment.
 */

import React, { useState } from 'react';
import { getPendingClaim } from './lib/archiveApi';
import { ARTIST_HEADLINE, ARTIST_PITCH } from './lib/artistCopy';
import posthog from 'posthog-js';
import { authFetch } from './lib/authFetch';
import { useRates, plus } from './lib/rates';
import './LandingPage.css';
import BrandMark from './components/BrandMark';
import { readReferralCode, clearReferralCode } from './lib/referral';
import { supabase } from './lib/supabaseClient';

// Neutral age screen: month + year only, no hint of the cut-off (FTC guidance). An ineligible
// answer is remembered on this device so the form can't simply be retried with a new answer.
const AGE_BLOCK_KEY = 'sd_signup_ineligible';
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 101 }, (_, i) => THIS_YEAR - i);
function readAgeBlock() { try { return localStorage.getItem(AGE_BLOCK_KEY) === '1'; } catch (_) { return false; } }
function writeAgeBlock() { try { localStorage.setItem(AGE_BLOCK_KEY, '1'); } catch (_) { /* storage blocked */ } }

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const PERSONAS = [
  {
    key:         'player',
    emoji:       '🎧',
    name:        'Player',
    tagline:     'The Fan',
    role:        'Discover artists, complete missions, and climb the Big Creative ranks.',
    earn:        'Earn SOUND by digging the Archive, sharing playlists, and recruiting creators.',
    agent:       'Rex',
    agentTitle:  'A&R Agent',
    color:       '#667eea',
    gradient:    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    glow:        'rgba(102, 126, 234, 0.4)',
  },
  {
    key:         'creator',
    emoji:       '🎤',
    name:        'Creator',
    tagline:     'The Artist',
    role:        'Build your fan base, post your content, and climb the creator stages.',
    earn:        'Earn SOUND for posting content and growing your fan base. FReN — Front Row collectibles for fans — coming soon.',
    agent:       'Ivy',
    agentTitle:  'Publicist',
    color:       '#f093fb',
    gradient:    'linear-gradient(135deg, #f093fb 0%, #e84393 100%)',
    glow:        'rgba(240, 147, 251, 0.4)',
  },
  {
    key:         'collector',
    emoji:       '💎',
    name:        'Collector',
    tagline:     'The Early Backer',
    role:        'Find artists early, back them before anyone else, and track who is next.',
    earn:        'Earn SOUND by digging the Archive, sharing playlists, and scouting artists. FReN — Front Row collectibles for fans — coming soon.',
    agent:       'Atlas',
    agentTitle:  'Portfolio Analyst',
    color:       '#fda085',
    gradient:    'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    glow:        'rgba(253, 160, 133, 0.4)',
  },
];

export default function PersonaOnboarding({ session, onConfirmed }) {
  const rates = useRates();
  // Sprint 6A Prompt 6: a band arriving from a claim page is a creator.
  const pendingClaim = getPendingClaim();
  const [selected,    setSelected]    = useState(pendingClaim ? 'creator' : null);
  const [city,        setCity]        = useState('');
  const [displayName, setDisplayName] = useState('');
  const [birthMonth,  setBirthMonth]  = useState('');
  const [birthYear,   setBirthYear]   = useState('');
  const [ageBlocked,  setAgeBlocked]  = useState(readAgeBlock);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);

  const handleConfirm = async () => {
    if (!selected || loading || !birthMonth || !birthYear || ageBlocked) return;
    setError(null);
    setLoading(true);

    try {
      // Prompt 1.5: /onboard takes the user id and email from the access token.
      // Wallets are no longer sent here — they are linked only through the
      // signed flow in lib/walletLink.js.
      // Referral code stored by InviteLanding when the user arrived via invite link
      // Claim signups never carry a referral: they pay nominators only on verification.
      const claimSignup     = selected === 'creator' && pendingClaim;
      const referralCode    = claimSignup ? undefined : (readReferralCode() || undefined);   // normalised; expired/malformed → none

      const res = await authFetch(`${API_BASE}/api/users/onboard`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona:    selected,
          referred_by: referralCode,   // backend resolves code → UUID
          claim_artist_id: claimSignup ? pendingClaim : undefined,
          city:         city.trim() || undefined,
          display_name: displayName.trim() || undefined,
          birth_month:  Number(birthMonth),
          birth_year:   Number(birthYear),
        }),
      });

      const data = await res.json();

      if (res.status === 403 && data.code === 'age_ineligible') {
        writeAgeBlock();
        setAgeBlocked(true);
        try { await supabase.auth.signOut(); } catch (_) { /* account already deleted server-side */ }
        return;
      }

      if (!res.ok) {
        setError(data.error || 'Onboarding failed. Please try again.');
        setLoading(false);
        return;
      }

      // Clean up stashed values now that they've been sent to /onboard
      clearReferralCode();   // Task 2.3: clear after use

      try { posthog.capture('onboarding_completed', { persona: selected, sound: data.sound_awarded }); } catch (_) { /* analytics non-fatal */ }

      // Callback passes the confirmed persona + sound_awarded to parent
      // Parent will show RewardMoment, then route to dashboard
      onConfirmed({
        user:          data.user,
        persona:       selected,
        sound_awarded: data.sound_awarded ?? 100,
        agent:         PERSONAS.find(p => p.key === selected),
      });

    } catch (err) {
      console.error('[PersonaOnboarding] onboard error:', err);
      setError('Connection error. Please check your internet and try again.');
      setLoading(false);
    }
  };

  if (ageBlocked) {
    return (
      <div className="persona-screen">
        <div className="persona-header">
          <div className="persona-logo"><BrandMark /></div>
          <p className="persona-subtitle">Sorry — you're not eligible to use SoundDAO.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="persona-screen">
      <div className="persona-header">
        <div className="persona-logo"><BrandMark /></div>
        <p className="persona-subtitle">
          This is your Big Creative identity in the SoundDAO universe.
        </p>
      </div>

      <div className="vp-section">
        {/* Hero */}
        <div className="vp-hero">
          {/* An artist arriving from a claim link sees artist copy: no earnings or speculation. */}
          {pendingClaim ? (
            <>
              <h2 className="vp-headline">{ARTIST_HEADLINE}</h2>
              <p className="vp-sub">{ARTIST_PITCH}</p>
            </>
          ) : (
            <>
              <h2 className="vp-headline">
                Discover artists. Back them early.<br />
                Earn SOUND when they blow up.
              </h2>
              <p className="vp-sub">
                The music game where fans earn SOUND by discovering
                independent artists, artists build their machine
                from Raw to Wildfire, and tastemakers spot
                who&#39;s next.
              </p>
            </>
          )}
        </div>
        {/* Stat pills */}
        <div className="vp-stats">
          <div className="vp-stat">
            <span className="vp-stat-num vp-orange">{plus(rates, 'recruit_creator')}</span>
            <span className="vp-stat-label">SOUND per artist recruited</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-purple">Raw→Wildfire</span>
            <span className="vp-stat-label">the creator arc</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-amber">Street team</span>
            <span className="vp-stat-label">fans who back you early</span>
          </div>
          <div className="vp-stat">
            <span className="vp-stat-num vp-green">Free</span>
            <span className="vp-stat-label">to join, always</span>
          </div>
        </div>
        <div className="vp-divider" />
        {/* Column headers */}
        <div className="vp-col-headers">
          <span className="vp-col-label vp-col-fan">For fans</span>
          <span className="vp-col-label vp-col-creator">For artists</span>
          <span className="vp-col-label vp-col-collector">For collectors</span>
        </div>
        {/* Props grid */}
        <div className="vp-grid">
          {/* Row 1 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">🎧</span>
            <div className="vp-card-title">Earn SOUND for discovering</div>
            <div className="vp-card-desc">Earn SOUND backing artists before everyone else does.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">🎤</span>
            <div className="vp-card-title">Build your machine</div>
            <div className="vp-card-desc">Post content, grow your street team, monetize your fanbase.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">💎</span>
            <div className="vp-card-title">Spot them first</div>
            <div className="vp-card-desc">Track rising artists by Heat Score before anyone else notices.</div>
          </div>
          {/* Row 2 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">🏆</span>
            <div className="vp-card-title">Climb Big Creative</div>
            <div className="vp-card-desc">Build your roster. The best scouts rise to the top.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">🔥</span>
            <div className="vp-card-title">Drop like a label</div>
            <div className="vp-card-desc">Schedule releases. Notify your street team. 3x SOUND for 24hrs.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">📈</span>
            <div className="vp-card-title">Follow the Heat</div>
            <div className="vp-card-desc">Heat Score is recalculated nightly. Watch who is actually moving.</div>
          </div>
          {/* Row 3 */}
          <div className="vp-card vp-card-fan">
            <span className="vp-card-icon">⚡</span>
            <div className="vp-card-title">Early fans earn most</div>
            <div className="vp-card-desc">First fans on a rising artist earn more than anyone who joins later.</div>
          </div>
          <div className="vp-card vp-card-creator">
            <span className="vp-card-icon">👥</span>
            <div className="vp-card-title">Fans invested in you</div>
            <div className="vp-card-desc">Your street team earns SOUND supporting you. They want you to win.</div>
          </div>
          <div className="vp-card vp-card-collector">
            <span className="vp-card-icon">🎵</span>
            <div className="vp-card-title">Support art you love</div>
            <div className="vp-card-desc">Win or lose, you&#39;re backing artists who deserve it.</div>
          </div>
        </div>
        <div className="vp-divider" />
        <p className="vp-identity-label">Now choose your role in the game:</p>
      </div>

      <h1 className="persona-title">Choose Your Identity</h1>

      <div className="persona-grid">
        {PERSONAS.map((p) => (
          <button
            key={p.key}
            className={`persona-card ${selected === p.key ? 'persona-card--selected' : ''}`}
            onClick={() => setSelected(p.key)}
            style={{
              '--persona-color':    p.color,
              '--persona-gradient': p.gradient,
              '--persona-glow':     p.glow,
            }}
          >
            <div className="persona-card-emoji">{p.emoji}</div>
            <h2 className="persona-card-name">{p.name}</h2>
            <p className="persona-card-tagline">{p.tagline}</p>
            <p className="persona-card-role">{p.role}</p>
            <div className="persona-card-earn">
              <span className="persona-earn-label">How you earn</span>
              <p className="persona-earn-text">{p.earn}</p>
            </div>
            <div className="persona-card-agent">
              <span className="persona-agent-badge">
                🤖 Your AI: {p.agent} · {p.agentTitle}
              </span>
            </div>
            {selected === p.key && (
              <div className="persona-selected-check">✓</div>
            )}
          </button>
        ))}
      </div>

      {/* City prompt — shown after persona selection */}
      {selected && (
        <div className="persona-city-row">
          <label className="persona-city-label" htmlFor="persona-city">
            📍 What city are you repping? <span className="persona-city-optional">(optional)</span>
          </label>
          <input
            id="persona-city"
            className="persona-city-input"
            type="text"
            placeholder="e.g. Providence, Atlanta, London…"
            value={city}
            onChange={e => setCity(e.target.value)}
            maxLength={100}
          />
        </div>
      )}

      {/* Display name prompt — shown after persona selection */}
      {selected && (
        <div className="persona-city-row">
          <label className="persona-city-label" htmlFor="persona-display-name">
            Choose your player name <span className="persona-city-optional">(optional)</span>
          </label>
          <input
            id="persona-display-name"
            className="persona-city-input"
            type="text"
            placeholder="How you'll appear on the leaderboard"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            maxLength={30}
          />
          <p style={{ fontSize: '11px', opacity: 0.4, marginTop: '4px' }}>
            You can change this later in settings.
          </p>
        </div>
      )}

      {/* Birth month + year — required, never stored */}
      {selected && (
        <div className="persona-city-row">
          <label className="persona-city-label" htmlFor="persona-birth-month">
            When were you born?
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select id="persona-birth-month" className="persona-city-input" aria-label="Birth month"
              value={birthMonth} onChange={e => setBirthMonth(e.target.value)}>
              <option value="">Month</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select id="persona-birth-year" className="persona-city-input" aria-label="Birth year"
              value={birthYear} onChange={e => setBirthYear(e.target.value)}>
              <option value="">Year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      )}

      {error && <p className="persona-error">{error}</p>}

      <div className="persona-footer">
        <button
          className="persona-confirm-btn"
          disabled={!selected || loading || !birthMonth || !birthYear}
          onClick={handleConfirm}
          style={selected ? {
            background: PERSONAS.find(p => p.key === selected)?.gradient,
            boxShadow:  `0 8px 32px ${PERSONAS.find(p => p.key === selected)?.glow}`,
          } : {}}
        >
          {loading
            ? 'Saving your identity…'
            : selected
              ? `I'm a ${PERSONAS.find(p => p.key === selected)?.name} — let's go →`
              : 'Select your identity'}
        </button>

        <p className="persona-warning">
          ⚠️ This is your Big Creative identity. Choose wisely — persona changes are not supported at launch.
        </p>
      </div>
    </div>
  );
}
